import { onCLS, onINP, onLCP } from 'web-vitals/attribution';

const ENGAGEMENT_MILESTONES = [15, 30, 60, 120];
const PENDING_LEAD_KEY = 'codeq.analytics.pendingLead';
const SCROLL_MILESTONES = [25, 50, 75, 90];
const TEXT_LIMIT = 100;

function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim().slice(0, TEXT_LIMIT);
}

function getPageType(root = document) {
    return root.documentElement?.dataset.analyticsPageType || 'unknown';
}

function getSectionContext(element, root = document) {
    const section = element?.closest?.('main > section, main > article');
    if (!section) {
        return {};
    }

    const sections = Array.from(root.querySelectorAll('main > section, main > article'));
    const heading = section.querySelector('h1, h2, h3');

    return {
        section_index: sections.indexOf(section) + 1,
        section_heading: normalizeText(heading?.textContent),
    };
}

function getLinkLocation(link) {
    if (link.closest('header')) return 'header';
    if (link.closest('footer')) return 'footer';
    return 'main';
}

function sendAnalyticsEvent(eventName, parameters = {}, targetWindow = window) {
    if (typeof targetWindow.gtag !== 'function') {
        return false;
    }

    targetWindow.gtag('event', eventName, {
        page_type: getPageType(targetWindow.document),
        ...parameters,
    });
    return true;
}

function attachClickTracking(root = document) {
    const onClick = (event) => {
        const accordionTrigger = event.target.closest('._faq-item__trigger');
        if (accordionTrigger) {
            queueMicrotask(() => {
                if (accordionTrigger.getAttribute('aria-expanded') === 'true') {
                    sendAnalyticsEvent('content_expand', {
                        item_title: normalizeText(accordionTrigger.textContent),
                        ...getSectionContext(accordionTrigger, root),
                    });
                }
            });
        }

        const link = event.target.closest('a[href]');
        if (!link) {
            return;
        }

        const href = link.getAttribute('href') || '';
        const commonParameters = {
            link_location: getLinkLocation(link),
            ...getSectionContext(link, root),
        };

        if (href.startsWith('mailto:')) {
            sendAnalyticsEvent('contact_click', { contact_method: 'email', ...commonParameters });
            return;
        }

        if (href.startsWith('tel:')) {
            sendAnalyticsEvent('contact_click', { contact_method: 'phone', ...commonParameters });
            return;
        }

        const url = new URL(link.href, root.location?.href || window.location.href);
        if (url.origin !== window.location.origin) {
            return;
        }

        const linkParameters = {
            link_text: normalizeText(link.textContent),
            link_path: url.pathname,
            ...commonParameters,
        };

        if (/\/(kontakt|contact)\/?$/i.test(url.pathname)) {
            sendAnalyticsEvent('contact_click', { contact_method: 'contact_page', ...linkParameters });
        }

        if (link.matches('.button, [data-analytics-cta]')) {
            sendAnalyticsEvent('cta_click', linkParameters);
        }
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
}

function attachSectionTracking(root = document) {
    if (typeof window.IntersectionObserver !== 'function') {
        return () => {};
    }

    const sections = Array.from(root.querySelectorAll('main > section, main > article'));
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                sendAnalyticsEvent('section_view', getSectionContext(entry.target, root));
                observer.unobserve(entry.target);
            });
        },
        { threshold: 0.5 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
}

function attachScrollTracking(root = document) {
    const reachedMilestones = new Set();

    const onScroll = () => {
        const scrollableHeight = root.documentElement.scrollHeight - window.innerHeight;
        if (scrollableHeight <= 0) return;

        const scrollDepth = Math.round((window.scrollY / scrollableHeight) * 100);
        SCROLL_MILESTONES.forEach((milestone) => {
            if (scrollDepth >= milestone && !reachedMilestones.has(milestone)) {
                reachedMilestones.add(milestone);
                sendAnalyticsEvent('scroll_depth', { percent_scrolled: milestone });
            }
        });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
}

function attachEngagementTracking(root = document) {
    let activeSeconds = 0;
    const reachedMilestones = new Set();
    const interval = window.setInterval(() => {
        if (root.visibilityState !== 'visible') return;

        activeSeconds += 1;
        ENGAGEMENT_MILESTONES.forEach((milestone) => {
            if (activeSeconds >= milestone && !reachedMilestones.has(milestone)) {
                reachedMilestones.add(milestone);
                sendAnalyticsEvent('content_engagement', { engagement_time_seconds: milestone });
            }
        });
    }, 1000);

    return () => window.clearInterval(interval);
}

function attachFormTracking(root = document) {
    const onSubmit = (event) => {
        if (event.target.matches('form')) {
            window.sessionStorage.setItem(PENDING_LEAD_KEY, String(Date.now()));
        }
    };

    root.addEventListener('submit', onSubmit);
    return () => root.removeEventListener('submit', onSubmit);
}

function trackConfirmedFormLead(root = document) {
    if (!window.sessionStorage.getItem(PENDING_LEAD_KEY)) {
        return;
    }

    root.querySelectorAll('[data-analytics-form-success]').forEach((marker) => {
        sendAnalyticsEvent('generate_lead', {
            method: 'form',
            form_name: marker.dataset.analyticsFormSuccess || 'contact',
        });
        window.sessionStorage.removeItem(PENDING_LEAD_KEY);
    });
}

function attachWebVitalsTracking() {
    const report = (metric) => {
        const target =
            metric.attribution?.target ||
            metric.attribution?.largestShiftTarget ||
            metric.attribution?.interactionTarget ||
            '';

        sendAnalyticsEvent('web_vital', {
            metric_name: metric.name,
            metric_value: metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value),
            metric_rating: metric.rating,
            metric_target: normalizeText(target),
        });
    };

    onCLS(report);
    onINP(report);
    onLCP(report);
}

function attachAnalyticsTracking(root = document) {
    trackConfirmedFormLead(root);
    attachWebVitalsTracking();

    const cleanups = [
        attachClickTracking(root),
        attachFormTracking(root),
        attachSectionTracking(root),
        attachScrollTracking(root),
        attachEngagementTracking(root),
    ];

    return () => cleanups.forEach((cleanup) => cleanup());
}

export {
    attachAnalyticsTracking,
    attachClickTracking,
    attachEngagementTracking,
    attachFormTracking,
    attachScrollTracking,
    attachSectionTracking,
    getSectionContext,
    normalizeText,
    sendAnalyticsEvent,
    trackConfirmedFormLead,
};
